import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AdminShell } from '@/apps/coach/components/admin/AdminShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { routes } from '@/lib/routes';
import { api } from '@/lib/api';

type UserDetailData = Awaited<ReturnType<typeof api.operator.users.get>>;

export function UserDetail() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<UserDetailData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    api.operator.users.get(id).then(setData).catch((err) => setError(err.message));
  }, [id]);

  if (error) {
    return (
      <AdminShell pageTitle="Nutzer-Details">
        <PageHeader title="Nutzer" showBack />
        <div className="flex-1 px-4 py-6"><p className="text-sm text-destructive">{error}</p></div>
      </AdminShell>
    );
  }

  if (!data) {
    return (
      <AdminShell pageTitle="Nutzer-Details">
        <PageHeader title="Nutzer" showBack />
        <div className="flex-1 px-4 py-6"><p className="text-sm text-muted-foreground">Lade...</p></div>
      </AdminShell>
    );
  }

  return (
    <AdminShell pageTitle="Nutzer-Details">
      <PageHeader title={data.name || data.email} showBack />
      <div className="flex-1 overflow-y-auto px-4 py-6 space-y-6">

        {/* User meta */}
        <section className="rounded-xl border border-border px-4 py-3 space-y-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{data.name || data.email}</p>
              <p className="text-xs text-muted-foreground truncate">{data.email}</p>
            </div>
            {data.isOperator && (
              <span className="shrink-0 text-[10px] uppercase tracking-wide bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                Operator
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            Registriert am {new Date(data.createdAt).toLocaleDateString()}
          </p>
        </section>

        {/* Redemptions */}
        <section className="space-y-2">
          <h2 className="text-sm font-semibold text-muted-foreground">Gutschein-Einlösungen</h2>
          {data.redemptions.length === 0 ? (
            <p className="text-sm text-muted-foreground">Noch keinen Gutschein eingelöst.</p>
          ) : (
            <div className="space-y-2">
              {data.redemptions.map((r) => {
                const isActive = !r.replacedAt && !r.voucherRevokedAt;
                return (
                  <Link
                    key={r.id}
                    to={routes.coach.operator.voucher(r.voucherCode)}
                    className="block rounded-xl border border-border px-4 py-3 space-y-1 hover:bg-muted transition-colors"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-mono text-sm font-semibold">{r.voucherCode}</p>
                      {isActive ? (
                        <span className="shrink-0 text-[10px] uppercase tracking-wide bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                          Aktiv
                        </span>
                      ) : r.voucherRevokedAt ? (
                        <span className="shrink-0 text-[10px] uppercase tracking-wide bg-destructive/10 text-destructive px-2 py-0.5 rounded-full">
                          Widerrufen
                        </span>
                      ) : (
                        <span className="shrink-0 text-[10px] uppercase tracking-wide bg-muted text-muted-foreground px-2 py-0.5 rounded-full">
                          Abgelöst
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {r.usedCount} / {r.voucherUsageLimit} Nachrichten
                      {r.voucherScope === 'global' && ' (gemeinsames Budget)'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Eingelöst am {new Date(r.redeemedAt).toLocaleDateString()}
                      {r.replacedAt && ` · abgelöst am ${new Date(r.replacedAt).toLocaleDateString()}`}
                    </p>
                  </Link>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </AdminShell>
  );
}
